import {
  Alert as MuiAlert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import dayjs, { TIMEZONE } from "../../utils/dayjs";
import useLocale from "../../utils/useLocale";
import {
  useGetUserDetailsQuery,
  useUpdateUserDetailsMutation,
} from "../userDetails/userDetailsApi";
import ParkingPermitSummary from "./components/ParkingPermitSummary";
import { useCreatePurchaseMutation, useGetProductsQuery } from "./purchaseApi";
import { GetUserDetailsResponse, Vehicle } from "@features/userDetails/types";
import { GetProductsResponse } from "./types";
import PurchaseAlert from "./components/PurchaseAlert.tsx";
import TermsAndConditionsDialog from "@features/login/components/TermsAndConditionsDialog";
import { useIsAuthenticated } from "@azure/msal-react";

function Purchase() {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const [selectedProductId, setSelectedProductId] = useState<string | null>("");

  const [addedVehicle, setAddedVehicle] = useState<Vehicle | null>(null);
  const [isAddingVehicle, setIsAddingVehicle] = useState<boolean>(false);

  const { data: userDetails, isLoading: isLoadingUserDetails } =
    useGetUserDetailsQuery(undefined);

  const productsQuery = useGetProductsQuery(undefined);

  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle>({
    licence_plate: "",
  });

  const selectedProduct = productsQuery.data?.find(
    (product) => product.id === selectedProductId,
  );

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [isTermsDialogOpen, setIsTermsDialogOpen] = useState(false);
  const [licensePlateError, setLicensePlateError] = useState(false);

  const licensePlateRequired = !selectedProduct?.permit_without_license_plates;
  const isEntraAuthenticated = useIsAuthenticated();

  const closeTermsDialog = () => {
    setIsTermsDialogOpen(false);
  };
  const showTermsAndConditions: React.MouseEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    event.preventDefault();

    setIsTermsDialogOpen(true);
  };

  useEffect(() => {
    if (!isLoadingUserDetails) {
      setSelectedVehicle(userDetails?.vehicles[0] ?? { licence_plate: "" });
      setIsAddingVehicle(userDetails?.vehicles.length == 0);
    }
  }, [isLoadingUserDetails, userDetails]);

  const [updateUserDetails, updateUserDetailsResult] =
    useUpdateUserDetailsMutation();
  const [createPurchase, createPurchaseResult] = useCreatePurchaseMutation();

  const onVehicleChange = async (vehicles: Vehicle[]) => {
    if (userDetails) {
      await updateUserDetails({
        ...userDetails,
        vehicles,
      }).unwrap(); // To throw the error
    }
  };

  const parkingPermitTypeSelectId = "parking-permit-type-select";
  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedProduct) {
      return;
    }
    // Reset both the mutation states so no stale error messages stay visible.
    // Depending on the product and user, both or just one mutation is called.
    updateUserDetailsResult.reset();
    createPurchaseResult.reset();
    if (!termsAccepted) {
      setTermsError(true);
      return;
    }
    setTermsError(false);
    // If virtual product we can just purchase without additional vehicle related operations
    if (!licensePlateRequired) {
      if (isEntraAuthenticated) {
        // If entra user, we have to update the user vehicles to make sure that user exists in database before purchasing
        void onVehicleChange(userDetails?.vehicles ?? [])
          .then(() => {
            void createPurchase({
              language: locale.toLocaleUpperCase(),
              vehicle: null,
              product_id: selectedProduct.id,
            });
          })
          .catch(() => {}); // Just need to catch, PurchaseAlert will show the error
      } else {
        void createPurchase({
          language: locale.toLocaleUpperCase(),
          vehicle: null,
          product_id: selectedProduct.id,
        });
      }
      return;
    }
    if (
      userDetails &&
      userDetails.vehicles.length === 0 &&
      !addedVehicle?.licence_plate.trim()
    ) {
      setLicensePlateError(true);
      return;
    }

    setLicensePlateError(false);
    if (isAddingVehicle && userDetails && addedVehicle?.licence_plate.trim()) {
      void onVehicleChange(userDetails.vehicles.concat(addedVehicle))
        .then(() => {
          void createPurchase({
            language: locale.toLocaleUpperCase(),
            vehicle: addedVehicle,
            product_id: selectedProduct.id,
          });
        })
        .catch(() => {}); // Just need to catch, PurchaseAlert will show the error
    } else {
      void createPurchase({
        language: locale.toLocaleUpperCase(),
        vehicle: selectedVehicle,
        product_id: selectedProduct.id,
      });
    }
  };

  return (
    <Stack padding={4} spacing={4}>
      <Typography variant="h5">{t("purchaseParkingPermit.title")}</Typography>
      <Typography variant="subtitle1" color="textSecondary">
        {t("purchaseParkingPermit.description")}
      </Typography>
      <PurchaseAlert
        error={createPurchaseResult.error || updateUserDetailsResult.error}
      />
      {productsQuery.isError && (
        <MuiAlert severity="error">
          {t("purchaseParkingPermit.productsQueryError")}
        </MuiAlert>
      )}
      {(productsQuery.isLoading || isLoadingUserDetails) && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}
      {productsQuery.isSuccess &&
        productsQuery.data.length > 0 &&
        userDetails && (
          <form onSubmit={onSubmit}>
            <Stack spacing={2}>
              <FormControl>
                <InputLabel id={parkingPermitTypeSelectId}>
                  {t("purchaseParkingPermit.parkingPermitType")}
                </InputLabel>
                <ProductSelector
                  labelId={parkingPermitTypeSelectId}
                  selectedProductId={selectedProductId}
                  products={productsQuery.data}
                  onSelectProduct={(selectedId) => {
                    setSelectedProductId(selectedId);
                  }}
                />
              </FormControl>
              {selectedProduct && (
                <ParkingPermitSummary
                  title={selectedProduct.title}
                  description={
                    locale == "en" && selectedProduct.description.EN
                      ? selectedProduct.description.EN
                      : locale == "sv" && selectedProduct.description.SV
                        ? selectedProduct.description.SV
                        : selectedProduct.description.FI
                  }
                  validFrom={
                    selectedProduct.permit_valid_from
                      ? dayjs(selectedProduct.permit_valid_from).tz(TIMEZONE)
                      : null
                  }
                  validDurationMinutes={
                    selectedProduct.permit_valid_duration_minutes
                  }
                  vatPercentage={selectedProduct.vat_rate}
                  unitPrice={selectedProduct.price_in_cents}
                  productType={selectedProduct.type}
                >
                  <Stack spacing={2}>
                    {licensePlateRequired && (
                      <VehiclesForm
                        isAddingVehicle={isAddingVehicle}
                        licensePlateError={licensePlateError}
                        userDetails={userDetails}
                        isLoadingUserDetails={isLoadingUserDetails}
                        selectedVehicle={selectedVehicle}
                        setAddedVehicle={setAddedVehicle}
                        setIsAddingVehicle={setIsAddingVehicle}
                        setSelectedVehicle={setSelectedVehicle}
                      />
                    )}
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={termsAccepted}
                            onChange={() => {
                              setTermsAccepted((prevState) => !prevState);
                            }}
                          />
                        }
                        label={
                          <Trans i18nKey="registerForm.acceptTerms" t={t}>
                            <Link
                              component="button"
                              onClick={showTermsAndConditions}
                            >
                              Sopimusehdot
                            </Link>
                            .
                          </Trans>
                        }
                      />
                      {termsError && (
                        <MuiAlert severity="warning">
                          {t("registerForm.acceptTermsRequired")}
                        </MuiAlert>
                      )}
                    </FormGroup>
                    <Button
                      disabled={
                        updateUserDetailsResult.isLoading ||
                        createPurchaseResult.isLoading ||
                        !selectedProductId
                      }
                      type="submit"
                      variant="contained"
                      sx={{
                        width: "fit-content",
                        marginLeft: "auto !important",
                      }}
                    >
                      {t("purchaseParkingPermit.submit")}
                    </Button>
                  </Stack>
                </ParkingPermitSummary>
              )}
            </Stack>
          </form>
        )}
      {productsQuery.isSuccess && productsQuery.data.length === 0 && (
        <MuiAlert severity="warning">
          {t("purchaseParkingPermit.noProducts")}
        </MuiAlert>
      )}
      <TermsAndConditionsDialog
        isOpen={isTermsDialogOpen}
        handleClose={closeTermsDialog}
      />
    </Stack>
  );
}

const ProductSelector = ({
  labelId,
  selectedProductId,
  products,
  onSelectProduct,
}: {
  labelId: string;
  selectedProductId: string | null;
  products: GetProductsResponse;
  onSelectProduct: (id: string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <Select
      labelId={labelId}
      label={t("purchaseParkingPermit.parkingPermitType")}
      sx={{ backgroundColor: "white" }}
      onChange={(event) => {
        onSelectProduct(event.target.value as string);
      }}
      value={selectedProductId}
      variant={"outlined"}
    >
      {products.map((product) => (
        <MenuItem key={product.id} value={product.id}>
          {product.title}
        </MenuItem>
      ))}
    </Select>
  );
};
interface VehiclesFormProps {
  isAddingVehicle: boolean;
  setIsAddingVehicle: React.Dispatch<React.SetStateAction<boolean>>;
  userDetails: GetUserDetailsResponse;
  setAddedVehicle: React.Dispatch<React.SetStateAction<Vehicle | null>>;
  isLoadingUserDetails: boolean;
  licensePlateError: boolean;
  selectedVehicle: Vehicle;
  setSelectedVehicle: React.Dispatch<React.SetStateAction<Vehicle>>;
}

function VehiclesForm({
  isAddingVehicle,
  setIsAddingVehicle,
  userDetails,
  setAddedVehicle,
  isLoadingUserDetails,
  licensePlateError,
  selectedVehicle,
  setSelectedVehicle,
}: VehiclesFormProps): React.ReactElement {
  const { t } = useTranslation();
  return (
    <>
      <Tabs
        value={isAddingVehicle}
        onChange={(_, newValue: boolean) => {
          setIsAddingVehicle(newValue);
        }}
      >
        <Tab
          label={t("vehiclesForm.chooseVehicle")}
          disabled={userDetails.vehicles.length < 1}
          value={false}
        />
        <Tab
          label={t("vehiclesForm.add")}
          value={true}
          disabled={userDetails.vehicles.length >= 3}
        />
      </Tabs>
      {isAddingVehicle ? (
        <TextField
          onChange={(event) => {
            setAddedVehicle({ licence_plate: event.target.value });
          }}
          label={t("vehiclesForm.licensePlate")}
          error={licensePlateError}
          helperText={licensePlateError ? t("vehiclesForm.addRequired") : null}
        />
      ) : (
        <FormControl>
          <InputLabel variant="outlined">
            {t("vehiclesForm.licensePlate")}
          </InputLabel>
          <Select
            data-testid="licence-plate-selector"
            notched
            required
            label={t("vehiclesForm.licensePlate")}
            value={selectedVehicle.licence_plate}
            onChange={(event) => {
              setSelectedVehicle({
                licence_plate: event.target.value,
              });
            }}
          >
            {isLoadingUserDetails
              ? null
              : userDetails.vehicles.map((vehicle) => (
                  <MenuItem
                    key={vehicle.licence_plate}
                    value={vehicle.licence_plate}
                  >
                    {vehicle.licence_plate}
                  </MenuItem>
                ))}
          </Select>
        </FormControl>
      )}
    </>
  );
}

export default Purchase;
